import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { Spin } from 'antd';
import SvgFormat from 'common/helpers/svgFormat';
import { QUERY_LANDLORD_PERSONAL_INFO } from '../queries';
import ContactInformationCard from './contact-information-card';
import FieldCard from './field-card';

const uesStyles = makeStyles(
  theme => ({
    root: {
      width: '100%',
      minHeight: 400,
      paddingLeft: 32,
      overflowY: 'auto',
    },
    titleWarp: {
      display: 'flex',
      boxShadow: theme.divider.dividerBottom,
    },
    title: {
      ...theme.typography.h3,
      lineHeight: '32px',
      marginRight: 12,
    },
    desc: {
      ...theme.typography.body2,
      lineHeight: '32px',
      color: theme.colors.captionColor,
    },
    accountImage: {
      width: 98,
      height: 98,
      marginTop: 22,
    },
    loading: {
      ...theme.flex.center,
      height: '100%',
    },
    content: {
      position: 'relative',
    },
    tipsWrap: {
      ...theme.typography.body4,
      position: 'absolute',
      top: 22,
      left: '60%',
      width: 266,
    },
  }),
  { name: 'PersonalInfo' },
);

export const Title = ({ title, desc }) => {
  const classes = uesStyles();

  return (
    <div className={classes.titleWarp}>
      <h3 className={classes.title}>{title}</h3>
      {desc && <span className={classes.desc}>{desc}</span>}
    </div>
  );
};

Title.propTypes = {
  title: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
};

const PersonalInfo = ({ userUuid }) => {
  const classes = uesStyles();
  const { t } = useTranslation();

  const [queryLandlord, { loading, data: personalInfo, refetch: refetchQueryLandlord }] =
    useLazyQuery(QUERY_LANDLORD_PERSONAL_INFO, {
      variables: { userUuid },
    });

  useEffect(() => {
    queryLandlord();
  }, [queryLandlord]);

  return (
    <div className={classes.root}>
      <Title title={t('personal_info')} />
      <Choose>
        <When condition={loading}>
          <Spin size="large" className={classes.loading} />
        </When>
        <Otherwise>
          <div className={classes.content}>
            <SvgFormat type="account-photo" className={classes.accountImage} />
            <FieldCard
              label={t('first_name')}
              value={personalInfo?.landlordContact?.firstName}
              type="firstName"
              id={personalInfo?.landlordContact?.id}
              enabled={personalInfo?.landlordContact?.enabled}
            />
            <FieldCard
              label={t('last_name')}
              value={personalInfo?.landlordContact?.lastName}
              type="lastName"
              id={personalInfo?.landlordContact?.id}
              enabled={personalInfo?.landlordContact?.enabled}
            />
            <ContactInformationCard
              landlordContact={personalInfo?.landlordContact ?? {}}
              refetchQueryLandlord={refetchQueryLandlord}
            />
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

PersonalInfo.propTypes = {
  userUuid: PropTypes.string.isRequired,
};

export default PersonalInfo;
